import React from 'react';
import { X, CheckSquare, ArrowsLeftRight } from 'phosphor-react';
import { SkinPhoto } from '../../types/entities';
import { formatDate } from '../../utils/date';
import usePresence from '../../use/usePresence';
import LazyImage from '../ui/LazyImage';

interface SkinPhotoSelectModalProps {
  aberto: boolean;
  photos: SkinPhoto[];
  compareIds: string[];
  toggleCompare: (id: string) => void;
  onCompare: () => void;
  onClose: () => void;
}

export default function SkinPhotoSelectModal({ aberto, photos, compareIds, toggleCompare, onCompare, onClose }: SkinPhotoSelectModalProps) {
  const { montado, estado } = usePresence(aberto, 200);

  if (!montado) return null;

  return (
    <div
      data-state={estado}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end justify-center p-4 data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out data-[state=closed]:pointer-events-none"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-state={estado}
        className="bg-white dark:bg-gray-800 w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl max-h-[85vh] flex flex-col data-[state=open]:animate-sheet-in data-[state=closed]:animate-sheet-out"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-4 border-b border-nude-100 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">
              Selecionar Fotos
            </h3>
            <p className="text-xs text-nude-400 dark:text-gray-500 mt-0.5">
              Escolha 2 fotos para comparar ({compareIds.length}/2)
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-nude-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="stagger overflow-y-auto flex-1 p-4 grid grid-cols-3 gap-3">
          {photos.map((photo) => {
            const isSelected = compareIds.includes(photo.id);
            return (
              <button
                key={photo.id}
                onClick={() => toggleCompare(photo.id)}
                className={`relative rounded-xl overflow-hidden aspect-square border-2 transition-colors active:scale-[0.97] ${
                  isSelected ? 'border-blush-400 shadow-md' : 'border-transparent'
                }`}
              >
                <LazyImage
                  src={photo.photoUrl}
                  alt={formatDate(photo.photoDate)}
                  className="w-full h-full"
                  imgClassName="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1.5">
                  <p className="text-white text-[10px] font-medium leading-none">
                    {formatDate(photo.photoDate)}
                  </p>
                </div>
                {isSelected && (
                  <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-blush-400 rounded-full flex items-center justify-center">
                    <CheckSquare className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="px-5 pb-6 pt-3 flex-shrink-0">
          <button
            disabled={compareIds.length < 2}
            onClick={onCompare}
            className="w-full py-3 bg-blush-400 hover:bg-blush-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <ArrowsLeftRight className="w-4 h-4" />
            Comparar {compareIds.length === 2 ? 'as 2 fotos' : `(${compareIds.length}/2)`}
          </button>
        </div>
      </div>
    </div>
  );
}
